/**
 * The arithmetic the report screens do to the server's answers.
 *
 * Everything here is pure and takes the wire types straight — no re-shaping into a
 * view model. Two reasons: the reports drill company → department → person through the
 * SAME summary type, so a helper written once works at all three widths; and a screen
 * that sorts a table has to re-run this on every keystroke, which is only cheap if it
 * is plain arithmetic over arrays already in memory.
 *
 * The one judgement call worth naming is what "attendance rate" divides by. The server
 * counts `scheduled_days` from the person's schedule, and a scope where nobody has a
 * schedule reports zero for it — every day lands in `unscheduled_days` instead.
 *
 * Both rates therefore return NULL for such a scope, and the screens print an em dash
 * with a line saying why. That is the third answer this file has given to the question,
 * and the reasoning is worth keeping:
 *
 *   - Dividing by `scheduled_days` alone printed 0%, which reads as "nobody came".
 *   - Falling back to present+absent printed 100% attendance AND 0% punctuality for the
 *     same scope — because with no schedule the engine never stamps `absent` and never
 *     stamps `onTime`. Two contradictory figures, both invented, side by side.
 *   - An em dash plus «график не задан» is the only version a reader cannot misread,
 *     and it points at the thing they can actually fix.
 */
import {
  EMPTY_SUMMARY,
  type AttendanceDayRow,
  type AttendanceEmployeeSummary,
  type AttendanceSummary,
} from '@/entities/attendance/types';
import {knownFlags} from '@/features/view-my-attendance/lib/flags';
import type {SortAccessors} from '@/shared/lib/table-sort';

/** Add up any number of roll-ups, field by field. */
export function sumSummaries(
  summaries: readonly AttendanceSummary[],
): AttendanceSummary {
  return summaries.reduce<AttendanceSummary>(
    (acc, s) => ({
      scheduled_days: acc.scheduled_days + s.scheduled_days,
      present_days: acc.present_days + s.present_days,
      absent_days: acc.absent_days + s.absent_days,
      on_time_days: acc.on_time_days + s.on_time_days,
      late_days: acc.late_days + s.late_days,
      early_arrival_days: acc.early_arrival_days + s.early_arrival_days,
      early_leave_days: acc.early_leave_days + s.early_leave_days,
      overtime_days: acc.overtime_days + s.overtime_days,
      unscheduled_days: acc.unscheduled_days + s.unscheduled_days,
      incomplete_days: acc.incomplete_days + s.incomplete_days,
      total_worked_minutes: acc.total_worked_minutes + s.total_worked_minutes,
    }),
    {...EMPTY_SUMMARY},
  );
}

/**
 * Share of scheduled days actually worked, 0..1 — or null when nothing was scheduled
 * and the question therefore has no answer. See the module note.
 */
export function attendanceRate(s: AttendanceSummary): number | null {
  return s.scheduled_days > 0 ? s.present_days / s.scheduled_days : null;
}

/**
 * Доля выходов, начавшихся без опоздания, 0..1 — или null, когда графика нет (вовремя
 * не бывает там, где не задано начало смены) либо никто не выходил.
 *
 * Считается ОТ ОПОЗДАНИЙ, а не от дней «вовремя», и это перестаёт быть одним и тем же,
 * как только движок проставил не все отметки. Живой отчёт на 1772 выходах пришёл с 1256
 * опозданиями и НУЛЁМ дней «вовремя»: 516 выходов не получили ни той отметки, ни другой.
 * Прежняя формула печатала «Без опозданий 0%» — то есть «опоздали все» — в тридцати
 * точках от счётчика «Опозданий 1256». Две цифры об одном и том же на одном экране,
 * противоречащие друг другу, и обе с претензией на точность.
 *
 * `present − late` такого не допускает: она делит на то же целое, что и счётчик рядом,
 * и сходится с ним по построению. Цена — день, которому движок не поставил ничего,
 * считается «не опоздал»; это правильная сторона для ошибки, потому что приписать
 * человеку опоздание, которого отчёт не зафиксировал, хуже, чем не приписать.
 *
 * `Math.max` — не украшение: отчёт приходит с сервера, а не из этого кода, и увидеть в
 * нём больше опозданий, чем выходов, значит напечатать отрицательный процент.
 *
 * Дни отсутствия сюда по-прежнему не входят: пропуск — вопрос явки, а не пунктуальности,
 * и, сложив их вместе, мы получили бы число, которое растёт, когда человек совсем
 * перестаёт приходить.
 */
export function punctualityRate(s: AttendanceSummary): number | null {
  return s.scheduled_days > 0 && s.present_days > 0
    ? Math.max(0, s.present_days - s.late_days) / s.present_days
    : null;
}

/** Average worked minutes per day actually present, or null if never present. */
export function averageDayMinutes(s: AttendanceSummary): number | null {
  return s.present_days > 0 ? s.total_worked_minutes / s.present_days : null;
}

/** A 0..1 share as a whole percent, or an em dash when there is nothing to show. */
export function formatRate(rate: number | null): string {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`;
}

/** One day of the whole scope, folded from every person's row for that date. */
export type DailyTotals = {
  date: string;
  present: number;
  onTime: number;
  late: number;
  absent: number;
  workedMinutes: number;
};

/**
 * Per-day totals across everybody, for the trend chart.
 *
 * `include_days=true` is what makes this possible at company width, and it is the
 * expensive call — see fetchCompanyAttendance. Rows arrive as a FLAT list (person ×
 * day), which is why this groups rather than transposes.
 */
export function dailyTotals(rows: readonly AttendanceDayRow[]): DailyTotals[] {
  const byDate = new Map<string, DailyTotals>();

  for (const row of rows) {
    let bucket = byDate.get(row.date);
    if (!bucket) {
      bucket = {
        date: row.date,
        present: 0,
        onTime: 0,
        late: 0,
        absent: 0,
        workedMinutes: 0,
      };
      byDate.set(row.date, bucket);
    }

    const flags = new Set(knownFlags(row.flags));
    // "Present" is having registered at all, which the event count says directly —
    // a person with no schedule is still at work, and `onTime`/`late` would both be
    // absent from their flags.
    if (row.event_count > 0) {
      bucket.present += 1;
    }
    if (flags.has('onTime')) {
      bucket.onTime += 1;
    }
    if (flags.has('late')) {
      bucket.late += 1;
    }
    if (flags.has('absent')) {
      bucket.absent += 1;
    }
    bucket.workedMinutes += row.worked_minutes ?? 0;
  }

  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** Every flag seen in a set of rows, with how often — the "what went wrong" ranking. */
export function flagCounts(
  rows: readonly AttendanceDayRow[],
): Array<{flag: string; count: number}> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const flag of knownFlags(row.flags)) {
      counts.set(flag, (counts.get(flag) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([flag, count]) => ({flag, count}))
    .sort((a, b) => b.count - a.count);
}

/** The columns an employee table can be ordered by. */
export type EmployeeSortKey =
  | 'name'
  | 'position'
  | 'present'
  | 'absent'
  | 'late'
  | 'overtime'
  | 'worked'
  | 'rate';

export type SortDirection = 'asc' | 'desc';

/**
 * Что лежит в каждом сортируемом столбце отчёта.
 *
 * Самих правил сортировки здесь нет: они общие на весь кабинет и живут в
 * [`shared/lib/table-sort`](../../../shared/lib/table-sort.ts). Раньше этот файл нёс
 * собственный компаратор — третий в приложении, — и расходился с остальными в мелочах,
 * которые видит только пользователь: пустая должность вставала в НАЧАЛО алфавита, а
 * «явка не посчитана» изображалась числом −1, то есть внизу при убывании и наверху
 * при возрастании.
 *
 * Теперь пустое — это `null`, и общее правило уносит его вниз в обе стороны: «нет
 * данных» не должно выглядеть ни худшим результатом, ни лучшим.
 */
export function employeeAccessors(): SortAccessors<AttendanceEmployeeSummary> {
  return {
    name: row => row.full_name,
    position: row => row.position || null,
    present: row => row.summary.present_days,
    absent: row => row.summary.absent_days,
    late: row => row.summary.late_days,
    overtime: row => row.summary.overtime_days,
    worked: row => row.summary.total_worked_minutes,
    rate: row => attendanceRate(row.summary),
  };
}

/**
 * Чем разбивать равенство в отчёте.
 *
 * Не номером строки: отчёт приходит в том порядке, в каком его собрал сервер, и этот
 * порядок сам по себе ничего не значит. У компании без графиков целый столбец —
 * сплошные нули, и без вторичного ключа люди в нём выстраивались бы как попало.
 */
export function employeeTiebreak(row: AttendanceEmployeeSummary): string {
  return row.full_name;
}

/** Какое замечание ищут в отчёте. `''` — не сужать. */
export type IssueFilter = '' | 'late' | 'absent' | 'earlyLeave' | 'clean' | 'silent';

/**
 * Люди, у которых за период есть замечание одного вида.
 *
 * ⚠️ **«Без замечаний» — это не «нули везде».** У человека без графика все три
 * счётчика нулевые: движок явки не считает опозданий там, где не знает, во сколько
 * начинается смена. Записать такого в образцовые значило бы похвалить за отсутствие
 * данных, поэтому «чистым» считается только тот, у кого есть хотя бы один рабочий день
 * по графику (`scheduled_days > 0`).
 *
 * Виды не сливаются в один «есть замечания»: пропуск, опоздание и ранний уход — три
 * разных разговора с тремя разными людьми.
 */
export function filterByIssue(
  rows: readonly AttendanceEmployeeSummary[],
  issue: IssueFilter,
): AttendanceEmployeeSummary[] {
  if (!issue) {
    return [...rows];
  }
  return rows.filter(({summary}) => {
    switch (issue) {
      case 'late':
        return summary.late_days > 0;
      case 'absent':
        return summary.absent_days > 0;
      case 'earlyLeave':
        return summary.early_leave_days > 0;
      case 'clean':
        return (
          summary.scheduled_days > 0 &&
          summary.late_days === 0 &&
          summary.absent_days === 0 &&
          summary.early_leave_days === 0
        );
      // Не «много пропусков», а НИ ОДНОЙ отметки за период. Отдельный вид, потому что
      // это чаще всего вопрос не к человеку, а к данным: терминала нет на его
      // проходной, либо он давно уволен и остался в списке. Плитка «ни разу не
      // отметились» ведёт сюда, и это единственный способ увидеть их поимённо.
      case 'silent':
        return summary.present_days === 0;
    }
  });
}

/** Free-text filter over the fields a person is recognisable by. */
export function filterEmployees(
  rows: readonly AttendanceEmployeeSummary[],
  search: string,
): AttendanceEmployeeSummary[] {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) {
    return [...rows];
  }
  return rows.filter(row =>
    [row.full_name, row.position, row.employee_number]
      .filter(Boolean)
      .some(field => field.toLocaleLowerCase().includes(needle)),
  );
}

/**
 * Кто попадает в «Требуют внимания».
 *
 * Отдельной функцией, потому что у списка есть подпись «показаны 5 из N», и N обязано
 * считаться ТЕМ ЖЕ правилом. Раньше подпись брала размер всего списка людей и выходила
 * фраза «Показаны 5 из 1304 — у остальных пропусков и опозданий нет» на отчёте, где
 * пропусков было семь тысяч. Одно правило на список и на его счёт — единственный
 * способ, которым такое не собирается заново.
 */
export function needsAttention(row: AttendanceEmployeeSummary): boolean {
  return row.summary.absent_days > 0 || row.summary.late_days > 0;
}

/** The people a report says are worth looking at first, worst attendance leading. */
export function worstPerformers(
  rows: readonly AttendanceEmployeeSummary[],
  limit: number,
): AttendanceEmployeeSummary[] {
  return rows
    .filter(needsAttention)
    .sort((a, b) => {
      const byAbsent = b.summary.absent_days - a.summary.absent_days;
      return byAbsent !== 0 ? byAbsent : b.summary.late_days - a.summary.late_days;
    })
    .slice(0, limit);
}

/**
 * Из кого сложился общий процент — разложение СПИСКА ЛЮДЕЙ, а не суммы дней.
 *
 * ## Зачем оно понадобилось
 *
 * Отчёт на 1304 человека показывал «Явка 20%», и это читалось как «четверо из пяти
 * прогуляли». На деле почти вся недостача сидела в людях, которые за период не
 * отметились НИ РАЗУ: график им задан, поэтому каждый их день попал в плановые, а в
 * присутствия — ни один. Пятьсот таких человек обваливают общий процент до цифры, по
 * которой уже ничего нельзя решить, и ничто на экране этого не объясняло.
 *
 * Разница практическая, а не терминологическая. Человек, вышедший пятнадцать дней из
 * двадцати, — это разговор с человеком. Человек, не отметившийся ни разу за восемь
 * дней, — это чаще всего терминал, которого нет на его проходной, уволенный, оставшийся
 * в списке, или график, назначенный тому, кто по нему не работает. Первое чинят
 * разговором, второе — данными, и отчёт обязан их различать.
 *
 * Считается по людям, а не по дням, и складывается не в 100%: `silent` — подмножество
 * тех, у кого есть замечания, потому что каждый их плановый день движок пометил
 * пропуском.
 */
export type RosterBreakdown = {
  /** Сколько людей в выборке. */
  total: number;
  /** Ни одной отметки за весь период. */
  silent: number;
  /** График не задан — таких движок не судит ни за опоздания, ни за пропуски. */
  unscheduled: number;
  /** У скольких человек есть хотя бы один пропуск. */
  absent: number;
  /** У скольких человек есть хотя бы одно опоздание. */
  late: number;
  /** Есть пропуски или опоздания — объединение двух предыдущих. */
  flagged: number;
  /** График есть, выходил, замечаний нет. */
  clean: number;
};

export function rosterBreakdown(
  rows: readonly AttendanceEmployeeSummary[],
): RosterBreakdown {
  let silent = 0;
  let unscheduled = 0;
  let absent = 0;
  let late = 0;
  let flagged = 0;
  let clean = 0;

  for (const row of rows) {
    const s = row.summary;
    if (s.present_days === 0) {
      silent += 1;
    }
    if (s.scheduled_days === 0) {
      unscheduled += 1;
    }
    if (s.absent_days > 0) {
      absent += 1;
    }
    if (s.late_days > 0) {
      late += 1;
    }
    if (needsAttention(row)) {
      flagged += 1;
    } else if (s.scheduled_days > 0) {
      // «Чистый» — только там, где было чему быть нарушенным: у человека без графика
      // нули в счётчиках означают «не оценивалось», а не «отработал безупречно».
      clean += 1;
    }
  }

  return {total: rows.length, silent, unscheduled, absent, late, flagged, clean};
}
